import React, {useEffect, useRef, useState, useMemo, useCallback} from 'react';
import Main from "../../modules/main/main";
import usePermissions from "../../services/auth/usePermissions";
import {useNavigate} from "react-router-dom";
import { AgGridReact } from 'ag-grid-react';
import ReactLoading from 'react-loading';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {apiPermissions} from "../../services/api/permissions";
import DialogModule from "../../modules/dialog/dialog";
import {useForm} from "react-hook-form";

function Permissions(props) {
    const navigate = useNavigate();
    const [userCan] = usePermissions();
    const breadcrumbs = [
        {"link": "/", "name": "Permissions", "active": true}
    ];

    const gridRef = useRef();
    const [open, setOpen] = React.useState(false);
    const [error, setError] = useState('');
    const [paginationPageSize, setPaginationPageSize] = useState(25);
    const [loading, setLoading] = useState(true);
    const isFetchingRef = useRef(false);
    const createDataSourceRef = useRef(null);
    const [editingPermission, setEditingPermission] = useState(null);
    
    const handleOpen = () => {
        setEditingPermission(null);
        reset();
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setEditingPermission(null);
        reset();
    };

    const {register, handleSubmit, reset, formState: {errors}} = useForm();

    const [columnDefs] = useState([
        {
            field: 'name',
            headerName: 'Name'
        },
        {
            field: 'slug',
            headerName: 'Slug'
        },
        {
            field: 'description',
            headerName: 'Description'
        },
        {
            field: 'model',
            headerName: 'Model'
        },
        {
            headerName: 'Action',
            cellRenderer: params => {
                if(userCan('edit.permissions') || userCan('view.permissions')) {
                    return (
                        <span>
                            <button className={'btn btn-primary mb-2 mr-2'} onClick={() => handleEdit(params.data)}>Edit</button>
                            <button className={'btn btn-danger mb-2'} onClick={() => handleDelete(params.data.id)}>Delete</button>
                        </span>
                    );
                }
            },
            editable: false,
            sortable: false,
            filter: false,
            minWidth: 200,
        }
    ]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        floatingFilter: true,
        suppressMenu: true,
        cellStyle: { display: 'flex', alignItems: 'center' }
    }), []);

    const handleEdit = (permission) => {
        setEditingPermission(permission);
        reset({
            name: permission.name,
            slug: permission.slug,
            description: permission.description || '',
            model: permission.model || ''
        });
        setOpen(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this permission?')) {
            try {
                await apiPermissions.delete(id);
                if (gridRef.current && gridRef.current.api && createDataSourceRef.current) {
                    const dataSource = createDataSourceRef.current();
                    gridRef.current.api.setDatasource(dataSource);
                }
            } catch (err) {
                setError('Failed to delete permission');
            }
        }
    };

    const onSave = async (data) => {
        setError('');
        try {
            let result;
            if (editingPermission) {
                // Add _method for Laravel to recognize PATCH request
                data = {...data, '_method': 'PATCH'};
                result = await apiPermissions.patch(editingPermission.id, data);
            } else {
                result = await apiPermissions.post(data);
            }
            
            // Check if result indicates success (has msg or permission property, and no error)
            if(result && (result.msg || result.permission) && !result.error && !result.status){
                // Success
                handleClose();
                if (gridRef.current && gridRef.current.api && createDataSourceRef.current) {
                    const dataSource = createDataSourceRef.current();
                    gridRef.current.api.setDatasource(dataSource);
                }
            } else {
                // Error - extract error message properly
                let errorMessage = 'An error occurred';
                if (result) {
                    if (result.error && typeof result.error === 'string') {
                        errorMessage = result.error;
                    } else if (result.message && typeof result.message === 'string') {
                        errorMessage = result.message;
                    } else if (result.data) {
                        if (typeof result.data === 'string') {
                            errorMessage = result.data;
                        } else if (typeof result.data === 'object') {
                            errorMessage = result.data.message || result.data.error || 'An error occurred';
                        }
                    } else if (typeof result === 'object' && result.message) {
                        errorMessage = result.message;
                    } else if (typeof result === 'string') {
                        errorMessage = result;
                    }
                }
                setError(errorMessage);
            }
        } catch (err) {
            // Extract error message from exception
            let errorMessage = 'Failed to save permission';
            if (err) {
                if (err.response && err.response.data) {
                    if (typeof err.response.data === 'string') {
                        errorMessage = err.response.data;
                    } else if (typeof err.response.data === 'object') {
                        errorMessage = err.response.data.message || err.response.data.error || 'Failed to save permission';
                    }
                } else if (err.message && typeof err.message === 'string') {
                    errorMessage = err.message;
                } else if (typeof err === 'object' && err.message) {
                    errorMessage = err.message;
                } else if (typeof err === 'string') {
                    errorMessage = err;
                }
            }
            setError(errorMessage);
        }
    };

    const createDataSource = useCallback(() => {
        return {
            getRows: async (params) => {
                try {
                    isFetchingRef.current = true;
                    setLoading(true);
                    
                    const page = Math.floor(params.startRow / paginationPageSize) + 1;
                    const sortModel = params.sortModel;
                    
                    let sortParams = null;
                    if (sortModel && sortModel.length > 0) {
                        sortParams = {
                            colId: sortModel[0].colId,
                            sort: sortModel[0].sort
                        };
                    }
                    
                    const queryParams = new URLSearchParams();
                    queryParams.append('page', page);
                    queryParams.append('per_page', paginationPageSize);
                    
                    if (sortParams) {
                        queryParams.append('sort_by', sortParams.colId || 'created_at');
                        queryParams.append('sort_order', sortParams.sort || 'desc');
                    }
                    
                    const queryString = queryParams.toString();
                    const res = await apiPermissions.getAll(queryString);
                    
                    let permissions = [];
                    let totalRows = 0;
                    
                    if (res && res.permissions) {
                        if (res.permissions.data && Array.isArray(res.permissions.data)) {
                            permissions = res.permissions.data;
                            totalRows = res.permissions.total || 0;
                        } else if (Array.isArray(res.permissions)) {
                            permissions = res.permissions;
                            totalRows = permissions.length;
                        }
                    } else if (Array.isArray(res)) {
                        permissions = res;
                        totalRows = res.length;
                    }
                    
                    if (!Array.isArray(permissions)) {
                        permissions = [];
                    }
                    
                    const lastRow = totalRows > 0 ? totalRows - 1 : 0;
                    params.successCallback(permissions, lastRow);
                    setLoading(false);
                    
                } catch (err) {
                    setError('Failed to load permissions');
                    params.failCallback();
                    setLoading(false);
                } finally {
                    isFetchingRef.current = false;
                }
            }
        };
    }, [paginationPageSize]);
    
    createDataSourceRef.current = createDataSource;

    useEffect(() => {
        if(!userCan('view.permissions')){
            navigate('/');
            return;
        }
    }, [userCan, navigate])

    return (
        <Main title="Permissions" breadcrumbs={breadcrumbs} loading={false}>
            {error && <div className="alert alert-danger">
                <h5><i className="icon fas fa-ban"></i> Error!</h5>
                {error}
            </div>}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                    <label htmlFor="pageSizeSelect" className="mr-2">Page Size:</label>
                    <select
                        id="pageSizeSelect"
                        className="form-control form-control-sm d-inline-block"
                        style={{ width: 'auto' }}
                        value={paginationPageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            setPaginationPageSize(newPageSize);
                            setLoading(true);
                            if (gridRef.current && gridRef.current.api) {
                                gridRef.current.api.paginationSetPageSize(newPageSize);
                                const dataSource = createDataSource();
                                gridRef.current.api.setDatasource(dataSource);
                                gridRef.current.api.paginationGoToFirstPage();
                            }
                        }}
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
                {userCan('create.permissions') &&
                    <button className={'btn btn-primary'} onClick={handleOpen}>Create Permission</button>
                }
            </div>
            <div className="ag-theme-alpine" style={{ height: 800, width: '100%', position: 'relative' }}>
                <div className={loading ? "overlay" : "overlay d-none"}>
                    <ReactLoading type="spin" color="orange" width={'10%'} height={'10%'} />
                </div>
                <AgGridReact
                    style={{ width: '100%', height: '100%' }}
                    ref={gridRef}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowModelType="infinite"
                    pagination={true}
                    paginationPageSize={paginationPageSize}
                    cacheBlockSize={paginationPageSize}
                    maxBlocksInCache={10}
                    getRowId={(params) => params.data.id}
                    onGridReady={(params) => {
                        if (!params.api) {
                            return;
                        }
                        try {
                            params.api.sizeColumnsToFit();
                            const dataSource = createDataSource();
                            if (dataSource && dataSource.getRows) {
                                params.api.setDatasource(dataSource);
                            }
                        } catch (error) {
                            // Error setting datasource
                        }
                    }}
                    onSortChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onFilterChanged={(params) => {
                        if (params.api && !isFetchingRef.current) {
                            params.api.purgeInfiniteCache();
                            params.api.paginationGoToFirstPage();
                            const dataSource = createDataSource();
                            params.api.setDatasource(dataSource);
                        }
                    }}
                    onColumnResized={(event) => event.api.sizeColumnsToFit()}
                    getRowHeight={() => 25}
                ></AgGridReact>
            </div>

            <DialogModule
                open={open}
                onClose={handleClose}
                setOpen={setOpen}
                title={editingPermission ? "Edit Permission" : "Create Permission"}
                size="md"
                fullWidth={true}
                hideButtons={true}
            >
                <form className="form-horizontal" onSubmit={handleSubmit(onSave)}>
                    {error && <div className="alert alert-danger">
                        <h5><i className="icon fas fa-ban"></i> Error!</h5>
                        {error}
                    </div>}
                    <div className="form-group row">
                        <label htmlFor="inputName" className="col-sm-2 col-form-label">Name</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                {...register('name', { required: 'Name is required' })}
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Permission Name"
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputSlug" className="col-sm-2 col-form-label">Slug</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                {...register('slug')}
                                className="form-control"
                                placeholder="permission-slug (auto-generated if empty)"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputDescription" className="col-sm-2 col-form-label">Description</label>
                        <div className="col-sm-10">
                            <textarea
                                {...register('description')}
                                className="form-control"
                                rows="3"
                                placeholder="Description"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <label htmlFor="inputModel" className="col-sm-2 col-form-label">Model</label>
                        <div className="col-sm-10">
                            <input
                                type="text"
                                {...register('model')}
                                className="form-control"
                                placeholder="Model (optional)"
                            />
                        </div>
                    </div>
                    <div className="form-group row">
                        <div className="offset-sm-2 col-sm-10">
                            <button type="submit" className="btn btn-primary">
                                {editingPermission ? 'Update' : 'Create'} Permission
                            </button>
                            <button type="button" className="btn btn-secondary ml-2" onClick={handleClose}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </form>
            </DialogModule>
        </Main>
    );
}

export default Permissions;

